import {
  type Category,
  CategorySubtype,
  CategoryType,
  type EntityCategory,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined } from "@digits-shared/helpers/filters"
import {
  type ActiveHierarchicalDimensionNode,
  convertToHierarchicalDimensionTree,
  type HierarchicalDimensionNode,
  sortHierarchicalDimensionByName,
} from "src/shared/helpers/hierarchicalDimensionHelper"

export const CATEGORY_TYPE_SORT_MAP = new Map<CategoryType, number>([
  [CategoryType.Assets, 1],
  [CategoryType.Liabilities, 2],
  [CategoryType.Equity, 3],
  [CategoryType.Income, 4],
  [CategoryType.CostOfGoodsSold, 5],
  [CategoryType.Expenses, 6],
  [CategoryType.OtherIncome, 7],
  [CategoryType.OtherExpenses, 8],
  [CategoryType.UnknownType, 100],
])

export const CATEGORY_SUBTYPE_SORT_MAP = new Map<CategorySubtype, number>([
  // Asset Subtypes
  [CategorySubtype.BankAccounts, 100],
  [CategorySubtype.AssetClearing, 105],
  [CategorySubtype.AccountsReceivable, 110],
  [CategorySubtype.AccruedRevenue, 115],
  [CategorySubtype.PrepaidExpenses, 120],
  [CategorySubtype.Inventory, 125],
  [CategorySubtype.Investments, 130],
  [CategorySubtype.ShareholderLoans, 135],
  [CategorySubtype.OtherCurrentAssets, 140],
  [CategorySubtype.UncategorizedAssets, 145],
  [CategorySubtype.FixedAssets, 150],
  [CategorySubtype.IntangibleAssets, 155],
  [CategorySubtype.Goodwill, 160],
  [CategorySubtype.OtherLongTermAssets, 170],
  // Liability Subtypes
  [CategorySubtype.AccountsPayable, 200],
  [CategorySubtype.CreditCards, 205],
  [CategorySubtype.LiabilityClearing, 210],
  [CategorySubtype.AccruedExpenses, 215],
  [CategorySubtype.DeferredRevenue, 220],
  [CategorySubtype.OtherCurrentLiabilities, 225],
  [CategorySubtype.ShortTermDebt, 230],
  [CategorySubtype.ConvertibleNotes, 235],
  [CategorySubtype.LongTermDebt, 240],
  [CategorySubtype.OtherLongTermLiabilities, 245],
  // Equity Subtypes
  [CategorySubtype.CommonStock, 300],
  [CategorySubtype.PreferredStock, 305],
  [CategorySubtype.Safes, 310],
  [CategorySubtype.OwnersEquity, 315],
  [CategorySubtype.TreasuryStock, 320],
  [CategorySubtype.AdditionalPaidInCapital, 325],
  [CategorySubtype.OtherComprehensiveIncome, 330],
  [CategorySubtype.RetainedEarnings, 335],
  [CategorySubtype.UncategorizedEquity, 340],
  // Income Subtypes
  [CategorySubtype.SalesRevenue, 400],
  [CategorySubtype.SubscriptionRevenue, 405],
  [CategorySubtype.TransactionRevenue, 410],
  [CategorySubtype.PartnerRevenue, 415],
  [CategorySubtype.RefundsAndDiscounts, 420],
  [CategorySubtype.UncategorizedIncome, 425],
  // Cost Of Goods Sold Subtypes
  [CategorySubtype.CostOfLabor, 500],
  [CategorySubtype.CostOfGoods, 505],
  [CategorySubtype.PaymentProcessingFees, 510],
  [CategorySubtype.HostingFees, 515],
  [CategorySubtype.OtherCostOfRevenue, 520],
  // Expenses Subtypes
  [CategorySubtype.PayrollExpenses, 600],
  [CategorySubtype.TravelAndEntertainment, 605],
  [CategorySubtype.ProfessionalFees, 610],
  [CategorySubtype.SalesAndMarketing, 615],
  [CategorySubtype.BusinessApplicationsAndSoftware, 620],
  [CategorySubtype.Facilities, 625],
  [CategorySubtype.SuppliesAndMaterials, 630],
  [CategorySubtype.GeneralOperations, 635],
  [CategorySubtype.UncategorizedExpense, 640],
  // Other Income Subtypes
  [CategorySubtype.InterestAndDividends, 800],
  [CategorySubtype.CreditCardRewards, 805],
  [CategorySubtype.OtherMiscellaneousIncome, 810],
  // Other Expenses Subtypes
  [CategorySubtype.DepreciationAndAmortization, 900],
  [CategorySubtype.BadDebtExpense, 905],
  [CategorySubtype.InterestExpense, 910],
  [CategorySubtype.Taxes, 915],
  [CategorySubtype.OtherMiscellaneousExpenses, 920],
  // Unknown Subtypes
  [CategorySubtype.UnknownSubtype, 1000],
])

export type CategoriesHierarchyNodesByType = {
  [key in CategoryType]: HierarchicalDimensionNode<Category | EntityCategory>[]
}

export interface ActiveCategory {
  category: EntityCategory
  count: number
}

export const getCategoriesHierarchyNodesByType = (nodes: Category[]) => {
  const categoriesTree = convertToHierarchicalDimensionTree(nodes, 0)
  return categoriesTree.reduce<CategoriesHierarchyNodesByType>(
    (types, categoryNode: HierarchicalDimensionNode<Category>) => {
      const typeNodes = types[categoryNode.dimension.type] || []
      typeNodes.push(categoryNode)
      types[categoryNode.dimension.type] = typeNodes
      return types
    },
    {} as CategoriesHierarchyNodesByType
  )
}

export const convertToActiveCategoriesTree = (
  nodes: ActiveCategory[],
  depth: number,
  parent?: ActiveCategory
) => {
  const directChildren = nodes
    .filter((node) => node.category.parentCategoryId === (parent ? parent.category.id : null))
    .sort(sortWithCategoriesByDisplayNumber)

  return directChildren.map((activeCategory) => {
    const categoryAssociation: ActiveHierarchicalDimensionNode<EntityCategory> = {
      depth,
      dimension: activeCategory.category,
      count: activeCategory.count,
      children: convertToActiveCategoriesTree(nodes, depth + 1, activeCategory),
    }
    categoryAssociation.children.forEach((c) => (c.parent = categoryAssociation))
    return categoryAssociation
  })
}

export const convertCategoryTreeToFlatListAndSort = (
  tree: (HierarchicalDimensionNode<Category> | undefined)[]
): HierarchicalDimensionNode<Category>[] => {
  // If all categories at this level have a numerical display number, use that as the primary sort mechanism
  const sortByCategoryNumberOnly = tree.every((node) =>
    node ? parsedCategoryDisplayNumber(node.dimension) !== undefined : true
  )

  return tree
    .filter(defined)
    .sort((a, b) =>
      sortByCategoryNumberOnly
        ? sortCategoriesByDisplayNumber(a.dimension, b.dimension)
        : sortCategories(a.dimension, b.dimension)
    )
    .reduce<HierarchicalDimensionNode<Category>[]>((allValues, element) => {
      if (element) allValues.push(element)
      return allValues.concat(convertCategoryTreeToFlatListAndSort(element?.children ?? []))
    }, [])
}

type WithCategory = { category: Category | EntityCategory }

/**
 * Sorts categories by type, subtype, then name
 */
export const sortCategories = (a: Category | EntityCategory, b: Category | EntityCategory) => {
  if (a.type !== b.type) {
    return (CATEGORY_TYPE_SORT_MAP.get(a.type) || 0) - (CATEGORY_TYPE_SORT_MAP.get(b.type) || 0)
  }

  if (a.subtype !== b.subtype) {
    return (
      (CATEGORY_SUBTYPE_SORT_MAP.get(a.subtype) || 0) -
      (CATEGORY_SUBTYPE_SORT_MAP.get(b.subtype) || 0)
    )
  }

  return sortCategoriesByDisplayNumber(a, b)
}

export const sortWithCategoriesByDisplayNumber = (
  withCategoryA: WithCategory,
  withCategoryB: WithCategory
) => sortCategoriesByDisplayNumber(withCategoryA.category, withCategoryB.category)

export const sortCategoriesByDisplayNumber = (
  categoryA: Category | EntityCategory,
  categoryB: Category | EntityCategory
) => {
  const displayNumberA = parsedCategoryDisplayNumber(categoryA)
  const displayNumberB = parsedCategoryDisplayNumber(categoryB)

  if (displayNumberA && !displayNumberB) return -1
  if (displayNumberB && !displayNumberA) return 1

  if (displayNumberA && displayNumberB) {
    return displayNumberA - displayNumberB
  }

  return sortHierarchicalDimensionByName(categoryA, categoryB)
}

export const categoryIdWithOther = (category: Category | EntityCategory) => {
  const isUglyOther = category.id.startsWith("other_")
  return { isUglyOther, categoryId: isUglyOther ? category.parentCategoryId : category.id }
}

export const isCategory = (category: Category | EntityCategory): category is Category =>
  (category as Category).categoryState !== undefined

export const parsedCategoryDisplayNumber = (category: Category | EntityCategory) => {
  const displayNumber = isCategory(category)
    ? category.displayNumber
    : category.chartOfAccountsDisplayNumber

  if (!displayNumber) return undefined

  const parsedNumber = parseInt(displayNumber?.replace("-", "") ?? "", 10)
  return isNaN(parsedNumber) ? undefined : parsedNumber
}
